import React, { useState, useEffect } from 'react';
import { MenuItem, FormControlLabel, TextField, Box, Menu, InputAdornment, Select, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { useToast } from 'use-toast-mui';
import { useTranslation } from 'react-i18next';

import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputPrice } from '../../../../form-components/FormInputPrice';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import FormInputAllSearch from '../../../../form-components/FormInputAllSearch';
import { filterTrainers } from '../../../../utils/methods';
import BranchSelectModal from '../../../common/modal/BranchSelectModal';
import AccessDoorSelect from '../../common/AccessDoorSelect';

import {
  CardsContainer,
  BasicInfoColumn,
  InfoCard,
  InfoCardTitle,
  InfoCardHeader,
  FieldGroup,
  FieldLabel,
  FieldLabelRow,
  FieldHint,
  InputUnit,
  HalfWidthContainer,
  HalfWidthField,
  RadioGroupRow,
  RadioOption,
  RadioCircleNew,
  RadioDotNew,
  RadioLabelNew,
  TagsContainer,
  Tag,
  TagText,
  TagCloseIcon,
  DayButtonsRow,
  DayButtonNew,
  TimePickerContainer,
  TimePickerGroup,
  TimeSelectSmall,
  TimeColon,
  TimeTilde,
  DatePickerRow,
  DatePickerField,
  CheckboxRow,
  CheckboxNew,
  CheckboxLabelNew,
  ToggleContainer,
  ToggleLabel,
  StyledSwitch,
  BottomActionsNew,
  CancelButtonNew,
  NextButtonNew,
  ErrorFieldWrapper,
  TimeSelect,
  SportTagPlaceholder,
  InfoCardHeaderRow,
  InfoCardHint,
  FieldLabelWithInfo,
  InfoIconSmall,
  GrayInputWrapper,
  DiscountUnitSelect,
  UsagePeriodInputBox,
} from './TicketEditStyled';
import SportTypeSelect from '../../../common/SportTypeSelect';

// 수업유형 라디오 2열 배치(개인·그룹 / 듀엣·OT), 열 기준으로 정렬
const TYPE_RADIO_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 120px)',
  columnGap: '24px',
  rowGap: '12px',
  height: 'auto',
};

function TicketEditStep01({
  control,
  watch,
  setValue,
  getValues,
  user,
  trainers,
  data,
  selectedDays,
  handleDayToggle,
  timeSettings,
  handleTimeChange,
  usagePeriodType = 'MONTH',
  setUsagePeriodType,
  isTaxFree,
  setIsTaxFree,
  discountType,
  setDiscountType,
  hasDiscountEndDate,
  setHasDiscountEndDate,
  handleRegularPriceChange,
  handleDiscountRateChange,
  handleFinalPriceChange,
  handleTrainerOnChange,
  onNext,
  onCancel,
  loader,
  DAYS,
  HOURS,
  MINUTES,
  CLASS_TYPES,
  centrerInfo,
  exposureYn,
  onExposureToggle,
  isEditMode,
  copySourceName = '',
  branchOptions = [],
  legacyBranchNames = {},
  selectedDoorIds,
  onDoorIdsChange,
  onDoorConvertedChange,
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const courseClassType = watch('courseClassType');
  const [errorFields, setErrorFields] = useState({});

  // 판매·이용지점 선택 모달 상태 (#73 판매/이용 분리 — 지점이 많아도 검색·전체선택 가능한 모달)
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [branchMenuField, setBranchMenuField] = useState(null); // 'saleBranchIds' | 'useBranchIds'
  const [sameAsSale, setSameAsSale] = useState(false);
  // 이용지점=구매한 지점(소속지점) 모드(#98) — 켜면 이용지점 목록 대신 회원이 결제한 지점 1곳만 이용
  const useSaleBranch = watch('useSaleBranchYn') === 'Y';
  const useBranchLocked = sameAsSale || useSaleBranch;
  const toggleUseSaleBranch = () => {
    const next = !useSaleBranch;
    setValue('useSaleBranchYn', next ? 'Y' : 'N');
    if (next) {
      setSameAsSale(false);
      setValue('useBranchIds', []);
    } else if (!(watch('useBranchIds') || []).length) {
      setValue('useBranchIds', watch('saleBranchIds') || []);
    }
  };
  const toggleSameAsSale = () => {
    if (!sameAsSale) setValue('useSaleBranchYn', 'N');
    setSameAsSale(!sameAsSale);
  };
  // 자동 차감 설정 — SUPERADMIN 항상, 그 외는 기업회원 자동 차감 설정(로그인 응답 autoDeductionYn)이 켜진 경우만.
  // 꺼진 곳도 기존에 설정된 상품 값은 그대로 유지되며, 화면 입력만 막는다.
  const canSeeAutoDeduction = user?.userRole === 'ROLE_SUPERADMIN' || user?.autoDeductionYn === 'Y';
  const myBranchIsDirect = branchOptions.some(option => option.branchId === user?.branch?.branchId);
  const isMultiBranch = branchOptions.length > 1 && myBranchIsDirect;
  const branchNameOf = branchId =>
    branchOptions.find(option => option.branchId === branchId)?.branchName
      ?? legacyBranchNames[branchId]
      ?? (branchId === user?.branch?.branchId ? user?.branch?.branchName : `지점 #${branchId}`);

  const branchFieldValue = field => (field ? watch(field) : []) || [];

  // 선택 요약 — 1개면 지점명 그대로, 여러 개면 "OO 외 N개"
  const branchSummaryText = field => {
    const ids = branchFieldValue(field);
    if (ids.length === 0) return null;
    if (ids.length === 1) return branchNameOf(ids[0]);
    return `${branchNameOf(ids[0])} 외 ${ids.length - 1}개`;
  };

  const openBranchModal = field => {
    setBranchMenuField(field);
    setBranchModalOpen(true);
  };

  // 이용지점 "판매지점과 동일" — 체크되어 있는 동안 판매지점 변경을 그대로 따라간다
  const sameAsSaleSourceKey = JSON.stringify(watch('saleBranchIds') || []);
  useEffect(() => {
    if (sameAsSale) {
      setValue('useBranchIds', JSON.parse(sameAsSaleSourceKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsSale, sameAsSaleSourceKey]);

  // 할인 단위 메뉴 상태
  const [discountUnitAnchor, setDiscountUnitAnchor] = useState(null);

  // 운동종목 옵션 변환 (i18n으로 한글 라벨 적용)
  const sportOptions =
    centrerInfo?.branchDetailInfo?.sportTypes?.map(type => ({
      label: t(type),
      value: type,
    })) || [];

  // 선택된 운동종목 (배열로 관리)
  const selectedSports = watch('sportsType')
    ? Array.isArray(watch('sportsType'))
      ? watch('sportsType')
      : [watch('sportsType')]
    : [];

  // 운동종목 선택 변경 — 빈 배열은 기존 규약대로 '' 로 저장한다
  const handleSportsChange = nextSports => {
    setValue('sportsType', nextSports.length > 0 ? nextSports : '');
  };

  // 필드 값 변경 시 해당 에러 제거
  const watchedFields = watch([
    'sportsType',
    'ticketName',
    'usageCount',
    'courseClassType',
    'classDefaultMinute',
    'usagePeriod',
    'regularPrice',
    'autoDeductionEnabled',
    'autoDeductionCount',
    'autoDeductionDayMode',
    'autoDeductionBaseDay',
  ]);
  useEffect(() => {
    const [sportsType, ticketName, usageCount, courseClassTypeValue, classDefaultMinute, usagePeriod, regularPrice,
      autoDeductionEnabled, autoDeductionCount, autoDeductionDayMode, autoDeductionBaseDay] =
      watchedFields;
    setErrorFields(prev => {
      const updated = { ...prev };
      // sportsType이 배열인 경우도 체크
      const hasSportsType = Array.isArray(sportsType) ? sportsType.length > 0 : !!sportsType;
      if (hasSportsType) delete updated.sportsType;
      if (ticketName) delete updated.ticketName;
      if (usageCount) delete updated.usageCount;
      if (courseClassTypeValue) delete updated.courseClassType;
      if (classDefaultMinute) delete updated.classDefaultMinute;
      if (usagePeriod) delete updated.usagePeriod;
      if (regularPrice) delete updated.regularPrice;
      if (!autoDeductionEnabled || Number(autoDeductionCount) >= 1) delete updated.autoDeductionCount;
      const dayValid = Number(autoDeductionBaseDay) >= 1 && Number(autoDeductionBaseDay) <= 31;
      if (!autoDeductionEnabled || autoDeductionDayMode === 'START' || dayValid) delete updated.autoDeductionBaseDay;
      return updated;
    });
  }, [
    watchedFields[0],
    watchedFields[1],
    watchedFields[2],
    watchedFields[3],
    watchedFields[4],
    watchedFields[5],
    watchedFields[6],
    watchedFields[7],
    watchedFields[8],
    watchedFields[9],
    watchedFields[10],
  ]);

  // 유효성 검사
  const validateForm = () => {
    const values = watch();
    const errors = {};

    // sportsType이 배열인 경우도 체크
    const hasSportsType = Array.isArray(values.sportsType) ? values.sportsType.length > 0 : !!values.sportsType;
    if (!hasSportsType) errors.sportsType = true;
    if (!values.ticketName) errors.ticketName = true;
    if (!values.usageCount) errors.usageCount = true;
    if (!values.courseClassType) errors.courseClassType = true;
    if (!values.classDefaultMinute) errors.classDefaultMinute = true;
    if (!values.usagePeriod) errors.usagePeriod = true;
    if (!values.regularPrice) errors.regularPrice = true;
    if (values.autoDeductionEnabled) {
      if (!(Number(values.autoDeductionCount) >= 1)) errors.autoDeductionCount = true;
      const day = Number(values.autoDeductionBaseDay);
      if (values.autoDeductionDayMode !== 'START' && !(day >= 1 && day <= 31)) {
        errors.autoDeductionBaseDay = true;
      }
    }

    setErrorFields(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.keys(errors)[0];
      const errorMessages = {
        sportsType: '운동종목을 선택해주세요',
        ticketName: '수강권명을 입력해주세요',
        usageCount: '이용횟수를 입력해주세요',
        courseClassType: '수업유형을 선택해주세요',
        classDefaultMinute: '수업시간을 입력해주세요',
        usagePeriod: '이용기간을 입력해주세요',
        regularPrice: '판매가를 입력해주세요',
        autoDeductionCount: '자동 차감 횟수를 입력해주세요',
        autoDeductionBaseDay: '자동 차감 일자를 1~31 사이로 입력해주세요',
      };
      toast.error(t(errorMessages[firstError]));
      return false;
    }
    return true;
  };

  // 다음 버튼 핸들러
  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  // 개인레슨 여부
  const isPrivateLesson =
    courseClassType === CLASS_TYPES.PRIVATE ||
    courseClassType === CLASS_TYPES.DUET ||
    courseClassType === CLASS_TYPES.OT;

  // 선택된 강사 목록 가져오기
  const selectedTrainerIds = watch('trainerIds') || [];
  const selectedTrainers = filterTrainers(trainers).filter(trainer =>
    selectedTrainerIds.some(selected => selected.value === trainer.value),
  );

  return (
    <>
      <CardsContainer>
        {/* 기본정보 + 출입설정을 한 칼럼으로 세로 정렬 */}
        <BasicInfoColumn>
          {/* Card 1: 기본정보 */}
          <InfoCard>
            <InfoCardHeader>
              <InfoCardTitle>기본정보</InfoCardTitle>
              {isEditMode && (
                <ToggleContainer>
                  <ToggleLabel>앱 노출</ToggleLabel>
                  <StyledSwitch
                    checked={exposureYn === 'Y'}
                    onChange={onExposureToggle}
                  />
                </ToggleContainer>
              )}
            </InfoCardHeader>

            {/* 수업유형 */}
            <FieldGroup>
              <FieldLabel>수업유형</FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.courseClassType} fullWidth>
                <RadioGroupRow sx={{ ...TYPE_RADIO_GRID_SX, ...(isEditMode ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}>
                  <RadioOption onClick={() => !isEditMode && setValue('courseClassType', CLASS_TYPES.PRIVATE)}>
                    <RadioCircleNew checked={courseClassType === CLASS_TYPES.PRIVATE}>
                      {courseClassType === CLASS_TYPES.PRIVATE && <RadioDotNew />}
                    </RadioCircleNew>
                    <RadioLabelNew>개인레슨</RadioLabelNew>
                  </RadioOption>
                  <RadioOption onClick={() => !isEditMode && setValue('courseClassType', CLASS_TYPES.GROUP)}>
                    <RadioCircleNew checked={courseClassType === CLASS_TYPES.GROUP}>
                      {courseClassType === CLASS_TYPES.GROUP && <RadioDotNew />}
                    </RadioCircleNew>
                    <RadioLabelNew>그룹레슨</RadioLabelNew>
                  </RadioOption>
                  <RadioOption onClick={() => !isEditMode && setValue('courseClassType', CLASS_TYPES.DUET)}>
                    <RadioCircleNew checked={courseClassType === CLASS_TYPES.DUET}>
                      {courseClassType === CLASS_TYPES.DUET && <RadioDotNew />}
                    </RadioCircleNew>
                    <RadioLabelNew>듀엣이상</RadioLabelNew>
                  </RadioOption>
                  <RadioOption onClick={() => !isEditMode && setValue('courseClassType', CLASS_TYPES.OT)}>
                    <RadioCircleNew checked={courseClassType === CLASS_TYPES.OT}>
                      {courseClassType === CLASS_TYPES.OT && <RadioDotNew />}
                    </RadioCircleNew>
                    <RadioLabelNew>OT</RadioLabelNew>
                  </RadioOption>
                </RadioGroupRow>
              </ErrorFieldWrapper>
            </FieldGroup>

            {/* 수강권명 */}
            <FieldGroup>
              <FieldLabel>수강권명</FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.ticketName} fullWidth>
                <FormInputText
                  control={control}
                  rules={{ required: true }}
                  name="ticketName"
                  placeholder={copySourceName ? `${copySourceName} (원본 이름)` : '수강권명을 입력해 주세요.'}
                />
              </ErrorFieldWrapper>
            </FieldGroup>

              {/* 판매지점·이용지점 (#73 판매/이용 분리 — 직영점 2곳 이상일 때만 다중 선택, 모달에서 체크박스로 선택) */}
              {isMultiBranch ? (
                <>
                  <FieldGroup>
                    <FieldLabelWithInfo>
                      <FieldLabel>판매지점</FieldLabel>
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>이 상품을 판매(등록)할 수 있는 지점입니다. 매출은 실제 결제가 등록된 지점에 귀속됩니다.</Box>
                        }
                        arrow
                        placement="top">
                        <InfoIconSmall>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                            <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                              ?
                            </text>
                          </svg>
                        </InfoIconSmall>
                      </Tooltip>
                    </FieldLabelWithInfo>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 14px',
                          border: '1px solid #E0E0E0',
                          borderRadius: '8px',
                          backgroundColor: '#F7F7F7',
                          fontSize: '14px',
                          color: '#333',
                        }}>
                        {branchSummaryText('saleBranchIds') || (
                          <SportTagPlaceholder>이 상품을 판매할 지점을 선택해 주세요.</SportTagPlaceholder>
                        )}
                      </Box>
                      <Box
                        onClick={() => openBranchModal('saleBranchIds')}
                        sx={{
                          flexShrink: 0,
                          padding: '0 14px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '8px',
                          border: '1px solid #4AB3BC',
                          color: '#4AB3BC',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          '&:hover': { backgroundColor: '#EAF7F8' },
                        }}>
                        지점 선택
                      </Box>
                    </Box>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabelWithInfo>
                      <FieldLabel>이용지점</FieldLabel>
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>구매한 회원이 실제로 출입·예약 등 이용할 수 있는 지점입니다. 판매지점과 달라도 됩니다. '구매한 지점만 이용'을 켜면 지점 목록 대신 회원이 결제한 지점 1곳에서만 이용합니다.</Box>
                        }
                        arrow
                        placement="top">
                        <InfoIconSmall>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                            <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                              ?
                            </text>
                          </svg>
                        </InfoIconSmall>
                      </Tooltip>
                    </FieldLabelWithInfo>
                    <CheckboxRow sx={{ marginBottom: '8px' }}>
                      <CheckboxNew checked={sameAsSale} onClick={toggleSameAsSale}>
                        {sameAsSale && (
                          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </CheckboxNew>
                      <CheckboxLabelNew>판매지점과 동일</CheckboxLabelNew>
                      {/* 소속지점 체크(#98)는 '판매지점과 동일' 옆에 나란히 */}
                      <Box sx={{ width: '20px', flexShrink: 0 }} />
                      <CheckboxNew checked={useSaleBranch} onClick={toggleUseSaleBranch}>
                        {useSaleBranch && (
                          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </CheckboxNew>
                      <CheckboxLabelNew>구매한 지점만 이용 (소속지점)</CheckboxLabelNew>
                    </CheckboxRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: sameAsSale ? 0.55 : 1 }}>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 14px',
                          border: '1px solid #E0E0E0',
                          borderRadius: '8px',
                          backgroundColor: '#F7F7F7',
                          fontSize: '14px',
                          color: '#333',
                        }}>
                        {useSaleBranch
                          ? '구매한 지점 (회원이 결제한 지점에서만 이용)'
                          : branchSummaryText('useBranchIds') || (
                              <SportTagPlaceholder>구매 회원이 이용 가능한 지점을 선택해 주세요.</SportTagPlaceholder>
                            )}
                      </Box>
                      <Box
                        onClick={() => !useBranchLocked && openBranchModal('useBranchIds')}
                        sx={{
                          flexShrink: 0,
                          padding: '0 14px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: '8px',
                          border: '1px solid #4AB3BC',
                          color: '#4AB3BC',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: useBranchLocked ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                          pointerEvents: useBranchLocked ? 'none' : 'auto',
                          '&:hover': { backgroundColor: '#EAF7F8' },
                        }}>
                        지점 선택
                      </Box>
                    </Box>
                  </FieldGroup>
                  <BranchSelectModal
                    open={branchModalOpen}
                    title={branchMenuField === 'saleBranchIds' ? '판매지점 선택' : '이용지점 선택'}
                    branches={branchOptions}
                    selectedIds={branchFieldValue(branchMenuField)}
                    onChangeIds={ids => setValue(branchMenuField, ids)}
                    onClose={() => setBranchModalOpen(false)}
                  />
                </>
              ) : (
                <FieldGroup>
                  <FieldLabel>판매지점</FieldLabel>
                  <FormInputDropdown
                    name="branchId"
                    control={control}
                    disabled
                    staticValue={user?.branch?.branchId}
                    options={[{ label: user?.branch?.branchName || '지점 선택', value: user?.branch?.branchId }]}
                    placeholder="판매지점을 선택해 주세요."
                  />
                </FieldGroup>
              )}

            {/* 운동종목 */}
            <FieldGroup>
              <FieldLabel>운동종목</FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.sportsType} fullWidth>
                <SportTypeSelect
                  options={sportOptions}
                  value={selectedSports}
                  onChange={handleSportsChange}
                  hasError={errorFields.sportsType}
                />
              </ErrorFieldWrapper>
            </FieldGroup>

            {/* 개인레슨/듀엣이상: 강사선택 */}
            {/* {isPrivateLesson && (
              <FieldGroup>
                <FieldLabel>강사선택</FieldLabel>
                <FormInputAllSearch
                  name="trainerIds"
                  control={control}
                  noOptionsText="선택할 강사가 없습니다"
                  handleChange={handleTrainerOnChange}
                  options={filterTrainers(trainers)}
                  placeholder="강사를 선택해 주세요"
                />
              </FieldGroup>
            )} */}
          </InfoCard>

          {/* Card: 출입설정 — 전환 지점에서만 노출. 미제한 문은 선택 고정 표시 */}
          <AccessDoorSelect
            branchId={user?.branch?.branchId}
            token={user?.token}
            selectedDoorIds={selectedDoorIds}
            onChange={onDoorIdsChange}
            onConvertedChange={onDoorConvertedChange}
            wrapper={content => (
              <InfoCard>
                <InfoCardTitle>출입설정</InfoCardTitle>
                {content}
              </InfoCard>
            )}
          />
        </BasicInfoColumn>

        {/* Card 2: 이용설정 */}
        <InfoCard>
          <InfoCardTitle>이용설정</InfoCardTitle>

          {/* 이용기간 */}
          <FieldGroup>
            <FieldLabelWithInfo>
              <FieldLabel>이용기간</FieldLabel>
              <Tooltip
                title={
                  <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>회원이 구매후 이용할 수 있는 기간을 설정합니다.</Box>
                }
                arrow
                placement="top">
                <InfoIconSmall>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                    <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                      ?
                    </text>
                  </svg>
                </InfoIconSmall>
              </Tooltip>
            </FieldLabelWithInfo>
            <UsagePeriodInputBox hasError={errorFields.usagePeriod}>
              <FormInputText
                className="usage-period-input"
                control={control}
                rules={{ required: true }}
                name="usagePeriod"
                placeholder="0"
              />
              <Select
                className="usage-period-select"
                value={usagePeriodType}
                onChange={e => setUsagePeriodType(e.target.value)}
                IconComponent={KeyboardArrowDownIcon}
                variant="standard"
                disableUnderline>
                <MenuItem value="MONTH">개월</MenuItem>
                <MenuItem value="DAY">일</MenuItem>
              </Select>
            </UsagePeriodInputBox>
          </FieldGroup>

          {/* 이용횟수 + 수업시간 */}
          <HalfWidthContainer>
            <HalfWidthField>
              <FieldGroup>
                <FieldLabel>이용횟수</FieldLabel>
                <ErrorFieldWrapper hasError={errorFields.usageCount} fullWidth>
                  <FormInputText
                    control={control}
                    rules={{ required: true }}
                    name="usageCount"
                    placeholder="이용횟수"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                        </InputAdornment>
                      ),
                    }}
                  />
                </ErrorFieldWrapper>
              </FieldGroup>
            </HalfWidthField>
            <HalfWidthField>
              <FieldGroup>
                <FieldLabel>수업시간</FieldLabel>
                <ErrorFieldWrapper hasError={errorFields.classDefaultMinute} fullWidth>
                  <FormInputText
                    control={control}
                    rules={{ required: true }}
                    name="classDefaultMinute"
                    placeholder="수업시간"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <InputUnit sx={{ marginRight: 0 }}>분</InputUnit>
                        </InputAdornment>
                      ),
                    }}
                  />
                </ErrorFieldWrapper>
              </FieldGroup>
            </HalfWidthField>
          </HalfWidthContainer>

          {/* 중지권 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabelWithInfo>
                <FieldLabel>중지권</FieldLabel>
                <Tooltip
                  title={
                    <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                      무제한: 중지 횟수·기간 제한 없음
                      <br />
                      중지 불가: 회원이 중지 신청 불가
                      <br />
                      직접 입력: 허용할 중지 횟수와 기간 지정
                    </Box>
                  }
                  arrow
                  placement="top">
                  <InfoIconSmall>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                      <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                        ?
                      </text>
                    </svg>
                  </InfoIconSmall>
                </Tooltip>
              </FieldLabelWithInfo>
            </FieldLabelRow>
            <RadioGroupRow>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'UNLIMITED');
                  setValue('stopCount', '');
                  setValue('stopPeriod', '');
                }}>
                <RadioCircleNew checked={watch('stopType') === 'UNLIMITED'}>
                  {watch('stopType') === 'UNLIMITED' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>무제한</RadioLabelNew>
              </RadioOption>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'DISABLED');
                  setValue('stopCount', 0);
                  setValue('stopPeriod', 0);
                }}>
                <RadioCircleNew checked={watch('stopType') === 'DISABLED'}>
                  {watch('stopType') === 'DISABLED' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>중지 불가</RadioLabelNew>
              </RadioOption>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'CUSTOM');
                  if (!watch('stopCount')) setValue('stopCount', 1);
                  if (!watch('stopPeriod')) setValue('stopPeriod', 30);
                }}>
                <RadioCircleNew checked={watch('stopType') === 'CUSTOM'}>
                  {watch('stopType') === 'CUSTOM' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>직접 입력</RadioLabelNew>
              </RadioOption>
            </RadioGroupRow>
            <HalfWidthContainer>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="stopCount"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('stopType') !== 'CUSTOM',
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="stopPeriod"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('stopType') !== 'CUSTOM',
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>일</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
            </HalfWidthContainer>
          </FieldGroup>

          {/* 이용제한 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabelWithInfo>
                <FieldLabel>이용제한</FieldLabel>
                <Tooltip
                  title={<Box sx={{ fontSize: 12, lineHeight: 1.5 }}>일일, 주간별 최대 이용 횟수를 설정합니다.</Box>}
                  arrow
                  placement="top">
                  <InfoIconSmall>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                      <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                        ?
                      </text>
                    </svg>
                  </InfoIconSmall>
                </Tooltip>
              </FieldLabelWithInfo>
              <CheckboxRow>
                <CheckboxNew
                  checked={watch('dailyUsageLimits') && watch('weeklyUsageLimits')}
                  onClick={() => {
                    const newValue = !(watch('dailyUsageLimits') && watch('weeklyUsageLimits'));
                    setValue('dailyUsageLimits', newValue);
                    setValue('weeklyUsageLimits', newValue);
                    if (newValue) {
                      setValue('dailyUsageLimit', '');
                      setValue('weeklyUsageLimit', '');
                    }
                  }}>
                  {watch('dailyUsageLimits') && watch('weeklyUsageLimits') && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>무제한</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <HalfWidthContainer>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="dailyUsageLimit"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('dailyUsageLimits'),
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>일일/회</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="weeklyUsageLimit"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('weeklyUsageLimits'),
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>주간/회</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
            </HalfWidthContainer>
          </FieldGroup>

          {/* 개인레슨: 요일설정 */}
          {isPrivateLesson && (
            <FieldGroup>
              <FieldLabel>요일설정</FieldLabel>
              <DayButtonsRow>
                {DAYS.map(day => (
                  <DayButtonNew
                    key={day.value}
                    selected={selectedDays.includes(day.value)}
                    onClick={() => handleDayToggle(day.value)}>
                    {day.label}
                  </DayButtonNew>
                ))}
              </DayButtonsRow>
            </FieldGroup>
          )}

          {/* 개인레슨: 시간대설정 */}
          {isPrivateLesson && (
            <FieldGroup>
              <FieldLabel>시간대설정</FieldLabel>
              <TimePickerContainer>
                <TimePickerGroup>
                  <TimeSelectSmall
                    value={timeSettings.startHour}
                    onChange={e => handleTimeChange('startHour', e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}>
                    {HOURS.map(h => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </TimeSelectSmall>
                  <TimeColon>:</TimeColon>
                  <TimeSelectSmall
                    value={timeSettings.startMinute}
                    onChange={e => handleTimeChange('startMinute', e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}>
                    {MINUTES.map(m => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TimeSelectSmall>
                </TimePickerGroup>
                <TimeTilde>~</TimeTilde>
                <TimePickerGroup>
                  <TimeSelectSmall
                    value={timeSettings.endHour}
                    onChange={e => handleTimeChange('endHour', e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}>
                    {HOURS.map(h => (
                      <MenuItem key={h} value={h}>
                        {h}
                      </MenuItem>
                    ))}
                  </TimeSelectSmall>
                  <TimeColon>:</TimeColon>
                  <TimeSelectSmall
                    value={timeSettings.endMinute}
                    onChange={e => handleTimeChange('endMinute', e.target.value)}
                    IconComponent={KeyboardArrowDownIcon}>
                    {MINUTES.map(m => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TimeSelectSmall>
                </TimePickerGroup>
              </TimePickerContainer>
            </FieldGroup>
          )}

          {/* 최대 예약권 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabelWithInfo>
                <FieldLabel>최대 예약권</FieldLabel>
                <Tooltip
                  title={
                    <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>동시에 예약할 수 있는 최대 횟수를 설정합니다.</Box>
                  }
                  arrow
                  placement="top">
                  <InfoIconSmall>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                      <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                        ?
                      </text>
                    </svg>
                  </InfoIconSmall>
                </Tooltip>
              </FieldLabelWithInfo>
              <CheckboxRow>
                <CheckboxNew
                  checked={watch('reservationCounts')}
                  onClick={() => {
                    const newValue = !watch('reservationCounts');
                    setValue('reservationCounts', newValue);
                    if (newValue) setValue('reservationCount', '');
                  }}>
                  {watch('reservationCounts') && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>무제한</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <FormInputText
              control={control}
              name="reservationCount"
              placeholder="0"
              InputProps={{
                readOnly: watch('reservationCounts'),
                endAdornment: (
                  <InputAdornment position="end">
                    <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                  </InputAdornment>
                ),
              }}
            />
          </FieldGroup>

          {/* 예약 취소권 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabelWithInfo>
                <FieldLabel>예약 취소권</FieldLabel>
                <Tooltip
                  title={
                    <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>예약을 취소할 수 있는 최대 횟수를 설정합니다.</Box>
                  }
                  arrow
                  placement="top">
                  <InfoIconSmall>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                      <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                        ?
                      </text>
                    </svg>
                  </InfoIconSmall>
                </Tooltip>
              </FieldLabelWithInfo>
              <CheckboxRow>
                <CheckboxNew
                  checked={watch('cancelCounts')}
                  onClick={() => {
                    const newValue = !watch('cancelCounts');
                    setValue('cancelCounts', newValue);
                    if (newValue) setValue('cancelCount', '');
                  }}>
                  {watch('cancelCounts') && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>무제한</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <FormInputText
              control={control}
              name="cancelCount"
              placeholder="0"
              InputProps={{
                readOnly: watch('cancelCounts'),
                endAdornment: (
                  <InputAdornment position="end">
                    <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                  </InputAdornment>
                ),
              }}
            />
          </FieldGroup>

          {canSeeAutoDeduction && (
            /* 자동 차감 */
            <FieldGroup>
              <FieldLabelRow>
                <FieldLabelWithInfo>
                  <FieldLabel>자동 차감</FieldLabel>
                  <Tooltip
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                        매월 실사용이 설정 횟수에 미달하면 부족분만큼 자동 차감합니다.
                        <br />
                        회원권 시작일 기준: 회원권 시작일과 같은 일자에 매월 차감
                        <br />
                        중지 기간에는 차감하지 않습니다.
                        <br />
                        설정을 변경해도 이미 판매된 회원권에는 적용되지 않습니다.
                      </Box>
                    }
                    arrow
                    placement="top">
                    <InfoIconSmall>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                        <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                          ?
                        </text>
                      </svg>
                    </InfoIconSmall>
                  </Tooltip>
                </FieldLabelWithInfo>
                <ToggleContainer>
                  <ToggleLabel>사용</ToggleLabel>
                  <StyledSwitch
                    checked={!!watch('autoDeductionEnabled')}
                    onChange={e => {
                      setValue('autoDeductionEnabled', e.target.checked);
                      if (!e.target.checked) {
                        setValue('autoDeductionCount', '');
                        setValue('autoDeductionBaseDay', '');
                      } else {
                        if (!watch('autoDeductionDayMode')) setValue('autoDeductionDayMode', 'DAY');
                        if (!watch('autoDeductionBaseDay')) setValue('autoDeductionBaseDay', 1);
                      }
                    }}
                  />
                </ToggleContainer>
              </FieldLabelRow>
              {watch('autoDeductionEnabled') && (
                <>
                  <ErrorFieldWrapper hasError={errorFields.autoDeductionCount} fullWidth>
                    <FormInputText
                      control={control}
                      name="autoDeductionCount"
                      placeholder="0"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <InputUnit sx={{ marginLeft: 0 }}>매월</InputUnit>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <InputUnit sx={{ marginRight: 0 }}>회 차감</InputUnit>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ErrorFieldWrapper>
                  <RadioGroupRow sx={{ height: '44px', gap: '12px', justifyContent: 'flex-start' }}>
                    <RadioOption
                      onClick={() => {
                        setValue('autoDeductionDayMode', 'START');
                        setValue('autoDeductionBaseDay', '');
                      }}>
                      <RadioCircleNew checked={watch('autoDeductionDayMode') === 'START'}>
                        {watch('autoDeductionDayMode') === 'START' && <RadioDotNew />}
                      </RadioCircleNew>
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                            회원이 이 상품을 구매할 때 지정한 수강권 시작 날짜와 연동됩니다.
                            <br />
                            시작일과 같은 일자에 매월 차감됩니다. (예. 15일 시작 → 매월 15일)
                          </Box>
                        }
                        arrow
                        placement="top">
                        <RadioLabelNew>회원권 시작일 기준</RadioLabelNew>
                      </Tooltip>
                    </RadioOption>
                    <RadioOption
                      onClick={() => {
                        setValue('autoDeductionDayMode', 'DAY');
                        if (!watch('autoDeductionBaseDay')) setValue('autoDeductionBaseDay', 1);
                      }}>
                      <RadioCircleNew checked={watch('autoDeductionDayMode') !== 'START'}>
                        {watch('autoDeductionDayMode') !== 'START' && <RadioDotNew />}
                      </RadioCircleNew>
                      <RadioLabelNew>고정 일수 기준</RadioLabelNew>
                    </RadioOption>
                  </RadioGroupRow>
                  {watch('autoDeductionDayMode') !== 'START' && (
                    <ErrorFieldWrapper hasError={errorFields.autoDeductionBaseDay} fullWidth>
                      <FormInputText
                        control={control}
                        name="autoDeductionBaseDay"
                        placeholder="1"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <InputUnit sx={{ marginLeft: 0 }}>매월</InputUnit>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <InputUnit sx={{ marginRight: 0 }}>일에 차감</InputUnit>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ErrorFieldWrapper>
                  )}
                </>
              )}
            </FieldGroup>
          )}
        </InfoCard>

        {/* Card 3: 금액설정 */}
        <InfoCard>
          <InfoCardTitle>금액설정</InfoCardTitle>

          {/* 판매금액 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>판매금액</FieldLabel>
              {/* <CheckboxRow>
                <CheckboxNew checked={isTaxFree} onClick={() => setIsTaxFree(!isTaxFree)}>
                  {isTaxFree && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>비과세 여부</CheckboxLabelNew>
              </CheckboxRow> */}
            </FieldLabelRow>
            <ErrorFieldWrapper hasError={errorFields.regularPrice} fullWidth>
              <FormInputPrice
                control={control}
                rules={{ required: true }}
                name="regularPrice"
                placeholder="0"
                type="tel"
                onChange={handleRegularPriceChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InputUnit sx={{ marginRight: 0 }}>원</InputUnit>
                    </InputAdornment>
                  ),
                }}
              />
            </ErrorFieldWrapper>
          </FieldGroup>

          {/* 할인금액 */}
          <FieldGroup>
            <FieldLabel>할인금액</FieldLabel>
            <FormInputPrice
              control={control}
              name="discountRate"
              placeholder="0"
              onChange={handleDiscountRateChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <DiscountUnitSelect onClick={e => setDiscountUnitAnchor(e.currentTarget)}>
                      <span className="unit-text">{discountType}</span>
                      <span className="unit-arrow">
                        <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      </span>
                    </DiscountUnitSelect>
                    <Menu
                      anchorEl={discountUnitAnchor}
                      open={Boolean(discountUnitAnchor)}
                      onClose={() => setDiscountUnitAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      <MenuItem
                        onClick={() => {
                          setDiscountType('원');
                          setDiscountUnitAnchor(null);
                        }}>
                        원
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setDiscountType('%');
                          setDiscountUnitAnchor(null);
                        }}>
                        %
                      </MenuItem>
                    </Menu>
                  </InputAdornment>
                ),
              }}
            />
          </FieldGroup>

          {/* 결제금액 */}
          <FieldGroup>
            <FieldLabel>결제금액</FieldLabel>
            <FormInputPrice
              control={control}
              name="finalPrice"
              placeholder="0"
              onChange={handleFinalPriceChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InputUnit sx={{ marginRight: 0 }}>원</InputUnit>
                  </InputAdornment>
                ),
              }}
            />
          </FieldGroup>
        </InfoCard>

        {/* Card 4: 기간설정 */}
        <InfoCard>
          <InfoCardTitle>기간설정</InfoCardTitle>

          {/* 판매기간 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>판매기간</FieldLabel>
              <CheckboxRow>
                <CheckboxNew
                  checked={watch('hasSaleEndDate')}
                  onClick={() => setValue('hasSaleEndDate', !watch('hasSaleEndDate'))}>
                  {watch('hasSaleEndDate') && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>종료일 없음</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <DatePickerRow>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="saleStartDate"
                    control={control}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
              <TimeTilde>~</TimeTilde>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="saleEndDate"
                    control={control}
                    disabled={watch('hasSaleEndDate')}
                    minDate={watch('saleStartDate')}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
            </DatePickerRow>
          </FieldGroup>

          {/* 할인기간 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>할인기간</FieldLabel>
              <CheckboxRow>
                <CheckboxNew checked={hasDiscountEndDate} onClick={() => setHasDiscountEndDate(!hasDiscountEndDate)}>
                  {hasDiscountEndDate && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>종료일 없음</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <DatePickerRow>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="discountStartDate"
                    control={control}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
              <TimeTilde>~</TimeTilde>
              <DatePickerField>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                  <FormInputDate
                    name="discountEndDate"
                    control={control}
                    disabled={hasDiscountEndDate}
                    renderInput={params => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </DatePickerField>
            </DatePickerRow>
          </FieldGroup>
        </InfoCard>
      </CardsContainer>

      {/* Bottom Buttons */}
      <BottomActionsNew>
        <CancelButtonNew onClick={onCancel}>취소</CancelButtonNew>
        <NextButtonNew onClick={handleNext} disabled={loader}>
          다음
        </NextButtonNew>
      </BottomActionsNew>
    </>
  );
}

export default TicketEditStep01;
